import React from 'react';
import { useSelector } from 'react-redux';

import type { ReduxState } from '../../../redux/types';

import { CURRENCY_BY_CODE } from 'app/constants/locales';
import { useTranslation } from 'app/lang';
import { accountFirstName, accountLastName } from 'app/lib/account/selectors';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';
import selectCurrency from 'app/redux/selectors/сurrency';

import NoFullNameLink from '../components/NoFullNameLink';
import { DISABLED_TITLE_STATUSES, FreebetPossibleStatus, REASON_BY_POSSIBLE_STATUS } from '../constants';
import { selectCanUseFreeBet } from '../selectors/entries';
import { selectSingleBetsInfo } from '../selectors/stakeTypes/single';

const { ASIAN_FREEBET_IS_FORBIDDEN, MATCH_FREEBET_IS_FORBIDDEN } = FreebetPossibleStatus;
const { onlyCurrency, matchFreebetIsSelected } = DISABLED_TITLE_STATUSES;

const useFreeBetsTitle = (
  betId: number,
) => {
  const { t } = useTranslation();
  const betInfo = useSelector((state: ReduxState) => selectSingleBetsInfo(state).get(betId));

  const currency = useSelector(selectCurrency);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const hasFullName = useSelector((state: ReduxState) => {
    return Boolean(accountFirstName(state) && accountLastName(state));
  });
  const canUseFreebet = useSelector((state: ReduxState) => selectCanUseFreeBet(state, betId));
  const maxValue = betInfo?.limits.max || 0;
  const minValue = betInfo?.limits.min || 0;
  const paymentOptions = betInfo?.paymentOptions;

  if (!canUseFreebet) {
    return t(matchFreebetIsSelected);
  }

  if (maxValue < minValue) {
    return t(onlyCurrency, { currency: CURRENCY_BY_CODE[currency] });
  }

  if (!(paymentOptions?.isFreeBetPossible && maxValue > 0)) {
    if (paymentOptions?.freeBetPossibleStatus === ASIAN_FREEBET_IS_FORBIDDEN) {
      return t(REASON_BY_POSSIBLE_STATUS[ASIAN_FREEBET_IS_FORBIDDEN]);
    }

    return t(REASON_BY_POSSIBLE_STATUS[MATCH_FREEBET_IS_FORBIDDEN]);
  }

  if (isLoggedIn && !hasFullName) {
    return <NoFullNameLink />;
  }
};

export default useFreeBetsTitle;
